import { Box, Container, Paper, Stack, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import VideocamIcon from "@mui/icons-material/Videocam";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import VerifiedIcon from "@mui/icons-material/Verified";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PaymentsIcon from "@mui/icons-material/Payments";
import { useAuth } from "@/auth/AuthContext";

type Step = {
  icon: React.ReactNode;
  title: string;
  text: string;
};

const patientSteps: Step[] = [
  {
    icon: <SearchIcon fontSize="large" />,
    title: "1. Find a doctor",
    text: "Browse verified doctors by specialization, city and ratings.",
  },
  {
    icon: <EventAvailableIcon fontSize="large" />,
    title: "2. Pick a slot",
    text: "Select a free 15-minute slot and optionally upload reports in advance.",
  },
  {
    icon: <ReceiptLongIcon fontSize="large" />,
    title: "3. Pay securely",
    text: "Pay in INR through our secure checkout — UPI, cards, netbanking or wallets.",
  },
  {
    icon: <VideocamIcon fontSize="large" />,
    title: "4. Consult over video",
    text: "Join from the web. Chat with the doctor, get a prescription, download reports.",
  },
];

const doctorSteps: Step[] = [
  {
    icon: <AssignmentIndIcon fontSize="large" />,
    title: "1. Complete your profile",
    text: "Add your qualification, years of experience, consultation fee and a short bio — that's what patients see on your profile card.",
  },
  {
    icon: <VerifiedIcon fontSize="large" />,
    title: "2. Submit for verification",
    text: "Share your medical-council registration and upload your credentials. Our admin team reviews each submission as per Indian medical rules — usually within 2 business days.",
  },
  {
    icon: <ScheduleIcon fontSize="large" />,
    title: "3. Set your weekly availability",
    text: "Once approved, define your consultation windows per day. We automatically generate 15-minute bookable slots inside those windows.",
  },
  {
    icon: <VideocamIcon fontSize="large" />,
    title: "4. Consult over video",
    text: "Patients book and pay online. Join the call from your dashboard, share prescriptions and notes, and move on to the next patient.",
  },
  {
    icon: <PaymentsIcon fontSize="large" />,
    title: "5. Get paid",
    text: "Consultation fees are settled to your linked account on a regular payout cycle. Every payment is itemised in your dashboard.",
  },
];

export default function HowItWorksPage() {
  const { isDoctor } = useAuth();
  const steps = isDoctor ? doctorSteps : patientSteps;

  const title = isDoctor
    ? "How ChikitsaLink works for doctors"
    : "How ChikitsaLink works";
  const subtitle = isDoctor
    ? "From signup to paid consultations in five simple steps."
    : "From booking to consultation in four simple steps.";

  return (
    <Container maxWidth="md" sx={{ py: { xs: 5, md: 8 } }}>
      <Typography variant="h3" sx={{ mb: 2 }}>
        {title}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 5 }}>
        {subtitle}
      </Typography>

      <Stack spacing={2}>
        {steps.map((s) => (
          <Paper key={s.title} variant="outlined" sx={{ p: 3 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2.5}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: 2,
                  bgcolor: "primary.main",
                  color: "#fff",
                  display: "grid",
                  placeItems: "center",
                  flex: "0 0 auto",
                }}
              >
                {s.icon}
              </Box>
              <Box>
                <Typography variant="h6" gutterBottom>
                  {s.title}
                </Typography>
                <Typography color="text.secondary">{s.text}</Typography>
              </Box>
            </Stack>
          </Paper>
        ))}
      </Stack>
    </Container>
  );
}
